import { Timestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { firebaseHelper } from "ui";

// The Web SDK can't fetch a subset of fields, so list reads that only need a
// few small fields go through Firestore REST `runQuery` with a field mask.
// Same security rules as the SDK; the caller falls back to the SDK on throw.

type FirestoreValue = Record<string, any>;

function decodeValue(value: FirestoreValue): any {
  if ("nullValue" in value) return null;
  if ("booleanValue" in value) return value.booleanValue;
  if ("integerValue" in value) return Number(value.integerValue);
  if ("doubleValue" in value) return Number(value.doubleValue);
  if ("stringValue" in value) return value.stringValue;
  if ("timestampValue" in value) return Timestamp.fromDate(new Date(value.timestampValue));
  if ("mapValue" in value) return decodeFields(value.mapValue.fields);
  if ("arrayValue" in value) return (value.arrayValue.values || []).map(decodeValue);
  throw new Error(`Unsupported Firestore value: ${Object.keys(value).join(",")}`);
}

function decodeFields(fields: Record<string, FirestoreValue> = {}): Record<string, any> {
  return Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, decodeValue(v)]));
}

export function decodeDocument(document: { name: string; fields?: Record<string, FirestoreValue> }): Record<string, any> {
  const data = decodeFields(document.fields);
  return { ...data, id: data.id ?? document.name.split("/").pop() };
}

export async function runMaskedQuery({
  collectionId,
  fields,
  inField,
  inValues,
  orderByDesc,
  andFilters,
  signal,
}: {
  collectionId: string;
  fields: readonly string[];
  inField: string;
  inValues: string[];
  orderByDesc: string;
  // Extra fieldFilters ANDed with the IN clause (team-scoped lists).
  andFilters?: Record<string, any>[];
  signal?: AbortSignal;
}): Promise<Record<string, any>[]> {
  const projectId = firebaseHelper.firebaseApp?.options?.projectId;
  if (!projectId) throw new Error("Firebase app not initialised");

  // Always as the signed-in user: the rules refuse an unauthenticated list.
  const user = getAuth(firebaseHelper.firebaseApp).currentUser;
  if (!user) throw new Error("runQuery needs a signed-in user");
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${await user.getIdToken()}`,
  };

  const inFilter = {
    fieldFilter: {
      field: { fieldPath: inField },
      op: "IN",
      value: { arrayValue: { values: inValues.map((stringValue) => ({ stringValue })) } },
    },
  };
  const structuredQuery = {
    select: { fields: fields.map((fieldPath) => ({ fieldPath })) },
    from: [{ collectionId }],
    where: andFilters?.length
      ? { compositeFilter: { op: "AND", filters: [inFilter, ...andFilters] } }
      : inFilter,
    orderBy: [{ field: { fieldPath: orderByDesc }, direction: "DESCENDING" }],
  };

  const res = await fetch(
    `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents:runQuery`,
    { method: "POST", headers, body: JSON.stringify({ structuredQuery }), signal },
  );
  const rows = await res.json();
  if (!res.ok || !Array.isArray(rows)) throw new Error(`runQuery failed: ${res.status}`);
  if (rows.some((row) => row.error)) throw new Error("runQuery returned an error");
  return rows.filter((row) => row.document).map((row) => decodeDocument(row.document));
}
